import React, { memo, useCallback, useMemo } from 'react'
import { GridColumnVisibilityModel } from '@mui/x-data-grid-pro'
import { useSelector } from 'react-redux'
import { EmptyDocusignTemplates } from 'src/components/docusign-template/grid/EmptyDocusignTemplates'
import { GridTable } from 'src/components/shared/grid/GridTable'
import {
  DOCUSIGN_TEMPLATES_GRID_COLUMNS_STORAGE_KEY,
  docusignTemplatePageSizeOptions,
} from 'src/constants/docusign-template/constants'
import { DocusignTemplatesTableFields } from 'src/constants/docusign-template/enums'
import { buildDocusignTemplateColumns } from 'src/constants/docusign-template/docusignTemplateGrid'
import type { DocusignTemplate } from 'src/interfaces/docusignTemplate'
import { getDocusignTemplates, getDocusignTemplatesLoading } from 'src/selectors/docusignTemplate'
import { filterDocusignTemplates } from 'src/utils/docusignTemplateList'
import { useGridColumns } from 'src/utils/hooks/grid/useGridColumns'
import { useGridPagination } from 'src/utils/hooks/grid/useGridPagination'

interface Props {
  searchQuery: string
  canManage: boolean
  onDeleteTemplate(template: DocusignTemplate): void
}

const getRowId = (row: DocusignTemplate) => {
  return row.id
}

// Title identifies the row and the actions column has no meaning when hidden; both also carry
// `hideable: false`, which is what removes them from the manage-columns panel. This guard
// additionally stops a programmatic visibility-model change from persisting a hide.
const UNHIDEABLE_COLUMNS: Array<string> = [
  DocusignTemplatesTableFields.title,
  DocusignTemplatesTableFields.actions,
]

const DocusignTemplatesTableComponent = ({ searchQuery, canManage, onDeleteTemplate }: Props) => {
  const templates = useSelector(getDocusignTemplates)
  const isLoading = useSelector(getDocusignTemplatesLoading)

  const { paginationModel, setPaginationModel } = useGridPagination(docusignTemplatePageSizeOptions)

  // A fresh array each render would reset the grid's column state, so the factory call is memoized.
  const docusignTemplateColumns = useMemo(() => {
    return buildDocusignTemplateColumns(onDeleteTemplate, canManage)
  }, [onDeleteTemplate, canManage])

  const { includedColumns, handleAddExcludedColumn, handleRemoveExcludedColumn } = useGridColumns(
    docusignTemplateColumns,
    DOCUSIGN_TEMPLATES_GRID_COLUMNS_STORAGE_KEY,
  )

  // The client holds every template (the endpoint takes no search param), so filtering is here.
  const visibleTemplates = useMemo(() => {
    return filterDocusignTemplates(templates, searchQuery)
  }, [templates, searchQuery])

  const columnVisibilityModel = useMemo(() => {
    return docusignTemplateColumns.reduce<GridColumnVisibilityModel>((accumulator, column) => {
      accumulator[column.field] = includedColumns.includes(column.field)

      return accumulator
    }, {})
  }, [docusignTemplateColumns, includedColumns])

  // `useGridColumns` stores excluded fields as a flat list; the grid speaks a visibility model.
  // Translate both ways so menu changes persist to localStorage across reloads.
  const handleColumnVisibilityModelChange = useCallback(
    (model: GridColumnVisibilityModel) => {
      docusignTemplateColumns.forEach((column) => {
        if (UNHIDEABLE_COLUMNS.includes(column.field)) {
          return
        }

        if (model[column.field] === false) {
          handleAddExcludedColumn(column.field)

          return
        }

        handleRemoveExcludedColumn(column.field)
      })
    },
    [docusignTemplateColumns, handleAddExcludedColumn, handleRemoveExcludedColumn],
  )

  const hasSearchQuery = searchQuery.trim() !== ''

  // `GridTable` renders EmptyElement with no props, so the search-aware copy is bound here.
  const EmptyElement = useMemo(() => {
    const BoundEmptyDocusignTemplates = () => {
      return <EmptyDocusignTemplates hasSearchQuery={hasSearchQuery} />
    }

    return BoundEmptyDocusignTemplates
  }, [hasSearchQuery])

  // Pinning is intentionally left uncontrolled, for the reason documented on CustomersTable:
  // `useGridColumns`'s persisted list has no left/right distinction.
  return (
    <GridTable<DocusignTemplate>
      rows={visibleTemplates}
      columns={docusignTemplateColumns}
      getRowId={getRowId}
      isLoading={isLoading}
      sortingMode="client"
      paginationMode="client"
      paginationModel={paginationModel}
      onPaginationModelChange={setPaginationModel}
      pageSizeOptions={docusignTemplatePageSizeOptions}
      initialState={{
        // The backend applies no ORDER BY at all (design §2), so this is the frontend choosing a
        // stable order — not mirroring the server's. Do not "fix" it to match one.
        sorting: { sortModel: [{ field: DocusignTemplatesTableFields.title, sort: 'asc' }] },
      }}
      columnVisibilityModel={columnVisibilityModel}
      onColumnVisibilityModelChange={handleColumnVisibilityModelChange}
      EmptyElement={EmptyElement}
      tableDataTestId="docusign-templates-list-table"
      rowDataTestIdPrefix="docusign-templates-list"
    />
  )
}

export const DocusignTemplatesTable = memo(DocusignTemplatesTableComponent)
